/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from 'react'
import type { TreeManager } from '@basehub/utils/tree/index'
import isEqual from 'lodash/isEqual'
import { sleep } from '~/lib/sleep'

/**
 * React hook to subscribe to changes in a specific slice of a tree manager.
 * The component will re-render only when the selected data changes.
 * The selector function is automatically memoized, so you don't need useCallback.
 *
 * @param treeManager - The tree manager to subscribe to
 * @param selector - Function to select the desired slice of data from the tree
 * @returns The selected data, updated when the relevant part of the tree changes
 */
export function useTreeSlice<T extends TreeManager<any, any>, Selected>(
  treeManager: T | null | undefined,
  selector: (tree: T) => Selected,
  equalityFn:
    | ((a: Selected, b: Selected) => boolean)
    | 'reference'
    | 'deep'
    | 'shallow' = 'reference',
  dependencies: unknown[] = [] // Add dependencies array parameter
): Selected | undefined {
  // Store selector in ref to avoid unnecessary re-renders
  const selectorRef = useRef(selector)
  // eslint-disable-next-line react-compiler/react-compiler
  selectorRef.current = selector

  // State to trigger re-renders
  const [state, setState] = useState<Selected | undefined>(() => {
    try {
      return treeManager
        ? // eslint-disable-next-line react-compiler/react-compiler
          selectorRef.current(treeManager)
        : undefined
    } catch (error) {
      return undefined
    }
  })

  const equalityFnRef = useRef(equalityFn)
  // eslint-disable-next-line react-compiler/react-compiler
  equalityFnRef.current = equalityFn

  // Store the previous value for comparison
  const prevValueRef = useRef<Selected | undefined>(state)

  // For tracking the subscription ID
  const subscriptionIdRef = useRef<string | null>(null)

  // Subscribe to tree manager changes and respond to dependency changes
  useEffect(() => {
    // Clean up previous subscription if treeManager changed
    if (subscriptionIdRef.current && treeManager) {
      treeManager.unsubscribe(subscriptionIdRef.current)
      subscriptionIdRef.current = null
    }

    // Nothing to subscribe to
    if (!treeManager) {
      setState(undefined)
      prevValueRef.current = undefined
      return
    }

    // Function to check and update state when the tree changes
    async function checkForUpdates() {
      try {
        let newValue: Selected | undefined
        let retries = 0
        while (!newValue) {
          try {
            // Use the current selector from ref
            newValue = selectorRef.current(treeManager!)
            break
          } catch (e) {
            retries++
            await sleep(16)
            if (retries >= 3) {
              throw e
            }
          }
        }

        // Create comparison function based on equalityFn parameter
        const equalityFn = equalityFnRef.current
        let areEqual: (
          a: Selected | undefined,
          b: Selected | undefined
        ) => boolean

        if (typeof equalityFn === 'function') {
          // Use custom equality function
          areEqual = equalityFn as (
            a: Selected | undefined,
            b: Selected | undefined
          ) => boolean
        } else if (equalityFn === 'deep') {
          // Use deep comparison with lodash isEqual
          areEqual = isEqual
        } else if (equalityFn === 'shallow') {
          // Use shallow comparison
          areEqual = (a, b) => {
            if (a === b) return true
            if (!a || !b) return false

            // For objects and arrays, compare shallow properties
            if (typeof a === 'object' && typeof b === 'object') {
              const keysA = Object.keys(a)
              const keysB = Object.keys(b)

              if (keysA.length !== keysB.length) return false

              return keysA.every(
                (key) => a[key as keyof Selected] === b[key as keyof Selected]
              )
            }

            return false
          }
        } else {
          // Use reference equality (default - 'reference')
          areEqual = (a, b) => a === b
        }

        // Compare using the determined equality function
        if (!areEqual(newValue, prevValueRef.current)) {
          prevValueRef.current = newValue
          setState(newValue)
        }
      } catch (error) {
        console.error('Error in tree slice selector:')
        console.error(error)
      }
    }

    // Set initial state
    checkForUpdates()

    // Subscribe to tree changes
    subscriptionIdRef.current = treeManager.subscribe(checkForUpdates)

    return () => {
      if (treeManager && subscriptionIdRef.current !== null) {
        treeManager.unsubscribe(subscriptionIdRef.current)
        subscriptionIdRef.current = null
      }
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeManager, ...dependencies]) // Note: selector is not a dependency because we use the ref

  return state
}
